import { useSignal } from "@preact/signals";
import { AuthLayout } from "../components/layout";
import { Button, Form, type FormErrors, Input } from "../components/ui";
import { usePageTitle } from "../hooks";
import { rpc } from "../rpc";

type FormState = {
  errors: Record<string, string>;
  loading: boolean;
  sent: boolean;
  email: string;
};

export function Page() {
  usePageTitle("Forgot Password");
  const state = useSignal<FormState>({
    errors: {},
    loading: false,
    sent: false,
    email: "",
  });

  // FormErrors-compatible lens for Form component
  const errors = {
    get value() {
      return state.value.errors;
    },
    set value(v: Record<string, string>) {
      state.value = { ...state.value, errors: v };
    },
  } as FormErrors;

  async function handleSubmit(e: Event) {
    e.preventDefault();
    const form = e.target as HTMLFormElement;
    const formData = new FormData(form);
    const emailValue = formData.get("email") as string;

    state.value = {
      ...state.value,
      errors: {},
      loading: true,
      email: emailValue,
    };

    try {
      await rpc.auth.sendLoginCode({ email: emailValue });
      state.value = { ...state.value, sent: true };
    } catch (err: unknown) {
      if (err && typeof err === "object" && "message" in err) {
        state.value = {
          ...state.value,
          loading: false,
          errors: { form: (err as Error).message },
        };
      } else {
        state.value = {
          ...state.value,
          loading: false,
          errors: { form: "Something went wrong" },
        };
      }
    }
  }

  if (state.value.sent) {
    return (
      <AuthLayout title="Check your email" hideLogo>
        <div class="text-center">
          <p class="text-text-muted">
            We sent a login link to{" "}
            <strong class="text-text">{state.value.email}</strong>. Click the
            link to sign in.
          </p>
          <p class="mt-6 text-sm text-text-muted">
            <a href="/login" class="link">
              Back to sign in
            </a>
          </p>
        </div>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout
      title="Sign in with email"
      subtitle="We'll send you a link to sign in instantly"
    >
      <Form errors={errors} onSubmit={handleSubmit} class="flex flex-col gap-6">
        {errors.value.form && (
          <div class="p-3 rounded-lg bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 text-sm">
            {errors.value.form}
          </div>
        )}

        <Input
          label="Email"
          name="email"
          type="email"
          placeholder="you@example.com"
          autocomplete="email"
          autofocus
          required
        />

        <Button type="submit" loading={state.value.loading}>
          Send sign-in link
        </Button>
      </Form>
      <p class="mt-6 text-center text-sm text-text-muted">
        Prefer a password?{" "}
        <a href="/login" class="link">
          Sign in here
        </a>
      </p>
    </AuthLayout>
  );
}
